/**
 * ParameterResolver.js
 * The Influence Engine: Resolves conflicts between Global, Local, and Trait-based parameters
 * using a Weighted Probabilistic approach.
 */

import { Random } from './Random.js';

export class ParameterResolver {
    constructor(random) {
        this.random = random || new Random();
    }

    /**
     * Resolves a Discrete Choice (Enum, String, Object) based on weighted probabilities.
     * Logic: Sum weights, pick random number in range, return corresponding value.
     * @param {Array<{value: any, weight: number, force?: boolean}>} candidates
     * @returns {any} The chosen value.
     */
    resolveDiscrete(candidates) {
        if (!candidates || candidates.length === 0) return null;

        // 1. Ultimate Authority Check
        const forced = candidates.find(c => c.force === true);
        if (forced) return forced.value;

        // Filter out zero weights
        const valid = candidates.filter(c => c.weight > 0);
        if (valid.length === 0) return candidates[0].value; // Fallback

        // Sum weights
        const totalWeight = valid.reduce((acc, c) => acc + c.weight, 0);

        // Roll dice
        let roll = this.random.next() * totalWeight;

        for (const candidate of valid) {
            if (roll < candidate.weight) return candidate.value;
            roll -= candidate.weight;
        }

        return valid[valid.length - 1].value;
    }

    /**
     * Resolves a Continuous Choice (Number, Velocity, Density).
     * Logic: Weighted Average Target + Gaussian Variance.
     * Variance is inversely proportional to total "Confidence" (sum of weights).
     * @param {Array<{value: number, weight: number, force?: boolean}>} candidates
     * @param {number} baseVariance - Standard deviation at weight=1.0
     * @returns {number} The resolved continuous value.
     */
    resolveContinuous(candidates, baseVariance = 0.1) {
        if (!candidates || candidates.length === 0) return 0;

        // 1. Ultimate Authority Check
        const forced = candidates.find(c => c.force === true);
        if (forced) return forced.value;

        const valid = candidates.filter(c => c.weight > 0);
        if (valid.length === 0) return candidates[0].value;

        let totalWeight = 0;
        let weightedSum = 0;

        valid.forEach(c => {
            weightedSum += c.value * c.weight;
            totalWeight += c.weight;
        });

        // Weighted Average Target
        const target = weightedSum / totalWeight;

        // Gaussian Variance
        // Logic: If totalWeight is high (strong consensus), variance is low.
        // If totalWeight is low (weak suggestions), variance is high.
        // Formula: stdDev = baseVariance / sqrt(totalWeight)
        // Limit variance clamping to avoid div by zero or extreme noise.
        const safeWeight = Math.max(0.1, totalWeight);
        const stdDev = baseVariance / Math.sqrt(safeWeight);

        // Gaussian Sample (Box-Muller)
        const u = 1 - this.random.next();
        const v = this.random.next();
        const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);

        return target + (z * stdDev);
    }
}
